<template>
	<template v-if="clickOnTestingButton">
		<DataTable
			class="p-component p-shadow-2"
			:value="fieldTestingValues"
			:autoLayout="true"
			:loading="loading"
			v-model:selection="selection"
			selectionMode="single"
			@row-select="onRowSelect"
		>
			<template #loading>
				{{  $t('Squeeze.General.Loading') }}
			</template>
			<template #empty>
				{{  $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.Validation.General.Position') }) }}
			</template>
			<Column field="id" header="ID" headerClass="header-center" style="width:3rem; text-align: center;" />
			<Column field="fieldName" :header="$t('Squeeze.DocumentClasses.Field')" />
			<Column field="keyWord.value" :header="$t('Squeeze.Training.Anchor')" style="min-width: 10rem;" />
			<Column field="value.value" :header="$t('Squeeze.Locators.Value')" style="min-width: 7rem;">
				<template #body="slotProps">
					<template v-if="!slotProps.data.value.value">
						-
					</template>
					<div class="column-cell" v-tooltip.bottom="slotProps.data.value.value">
						{{ slotProps.data.value.value }}
					</div>
				</template>
			</Column>
			<Column style="min-width: 7rem; max-width: 7rem; text-align: right;">
				<template #body="slotProps">
					<template v-if="slotProps.data.dataType === 'Amount' || slotProps.data.dataType === 'Date'">
						<span v-tooltip="$t('Squeeze.Training.EditButtonDisabled')">
							<Button  icon="mdi mdi-pencil-outline" :disabled="true" class="p-button p-component p-button-icon-only p-button-rounded p-button-success p-button-text" />
						</span>
					</template>
					<template v-else>
						<Button v-tooltip="$t('Squeeze.Locators.Edit')" icon="mdi mdi-pencil-outline" class="p-button p-component p-button-icon-only p-button-rounded p-button-success p-button-text" @click="changeTrainingRow(slotProps.data)"/>
					</template>

					<Button v-tooltip="$t('Squeeze.Validation.Buttons.Delete')" icon="mdi mdi-delete-outline" class="p-button p-button-rounded p-button-danger p-button-text deleteButtonRow" @click="deleteTrainingRow(slotProps.data)"/>
				</template>
			</Column>
		</DataTable>
	</template>
	<template v-else>
		<DataTable class="p-component p-shadow-2" :value="tableTrainingValues" :autoLayout="true" :rowHover="true" :loading="loading">
			<template #loading>
				{{  $t('Squeeze.General.Loading') }}
			</template>
			<template #empty>
				{{  $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.Validation.General.Position') }) }}
			</template>
			<Column field="id" header="ID" headerClass="header-center" style="width:3rem; text-align: center;" />
			<Column field="fieldName" :header="$t('Squeeze.DocumentClasses.Field')">
				<template #body="slotProps">
					<div class="p-d-flex p-justify-between p-ai-center">
						<span>{{slotProps.data.fieldName}}</span>
						<span
							v-if="checkIfFieldIsNotTrainable(slotProps.data.fieldName)"
							v-tooltip="$t('Squeeze.Training.NotTrainable', { fieldName: slotProps.data.fieldName})"
							class="icon-error p-ml-2"
						>
							<i class="mdi mdi-alert-outline"></i>
						</span>
					</div>
				</template>
			</Column>
			<Column field="keyWordPattern" :header="$t('Squeeze.Training.Anchor')" />
			<Column field="valuePattern" :header="$t('Squeeze.Locators.Value')" style="min-width: 10rem;" />
			<Column field="replaceValue" :header="$t('Squeeze.Training.ResultValue')" style="min-width: 10rem; max-width: 10rem;">
				<template #body="slotProps">
					<template v-if="!slotProps.data.replaceValue">
						-
					</template>
					<div v-else class="column-cell" v-tooltip.bottom="slotProps.data.replaceValue">
						{{ slotProps.data.replaceValue }}
					</div>
				</template>
			</Column>
			<Column style="min-width: 7rem; max-width: 7rem; text-align: right;">
				<template #body="slotProps">
					<template v-if="slotProps.data.dataType === 'Amount' || slotProps.data.dataType === 'Date'">
						<span v-tooltip="$t('Squeeze.Training.EditButtonDisabled')">
							<Button :disabled="true" icon="mdi mdi-pencil-outline" class="p-button p-component p-button-icon-only p-button-rounded p-button-success p-button-text" />
						</span>
					</template>
					<template v-else>
						<Button v-tooltip="$t('Squeeze.Locators.Edit')" icon="mdi mdi-pencil-outline" class="p-button p-component p-button-icon-only p-button-rounded p-button-success p-button-text" @click="changeTrainingRow(slotProps.data)"/>
					</template>
					<Button v-tooltip="$t('Squeeze.Validation.Buttons.Delete')" icon="mdi mdi-delete-outline" class="p-button p-button-rounded p-button-danger p-button-text deleteButtonRow" @click="deleteTrainingRow(slotProps.data)"/>
				</template>
			</Column>
		</DataTable>
	</template>
</template>

<script lang="ts">
import {getTranslatedDescription} from "@/util/TranslationHelper";
import {defineComponent, PropType, ref} from "vue";
import {useI18n} from "vue-i18n";
import Button from "primevue/button";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {FieldTrainingWithName} from "@/apps/squeeze/views/HeadTraining.vue";
import {DocumentField} from "@dex/squeeze-client-ts";

export default defineComponent({
	name: "DocumentFieldTrainingTable",
	components: {
		Button,
		DataTable,
		Column,
	},
	props: {
		fieldTestingValues: {
			type: Array as PropType<FieldTrainingWithName[]>,
			default: () => [],
		},
		tableTrainingValues: {
			type: Array as PropType<FieldTrainingWithName[]>,
			default: () => [],
		},
		clickOnTestingButton: {
			type: Boolean,
			default: false,
		},
		loading: {
			type: Boolean,
			default: false,
		},
		documentClassFields: {
			type: Array as PropType<DocumentField[]>,
			default: () => [],
		},
	},
	emits: [
		'deleteTrainingRow',
		'changeTrainingRow',
		'onMarkRegion',
	],
	setup(props, {emit}) {
		const {t} = useI18n();

		/** Currently-Selected row */
		const selection = ref(null);

		/**
		 * Deletes a row of the training table
		 * @param {FieldTrainingWithName} rowData
		 */
		const deleteTrainingRow = (rowData: FieldTrainingWithName) => {
			emit("deleteTrainingRow", rowData);
		}

		/**
		 * Change a training value
		 * @param {FieldTrainingWithName} rowData
		 */
		const changeTrainingRow = (rowData: FieldTrainingWithName) => {
			emit("changeTrainingRow", rowData);
		}

		/**
		 * Click a testing row to mark
		 * @param event
		 */
		const onRowSelect = (event: any) => {
			emit("onMarkRegion", event);
		}

		/**
		 * Check if the field is not trainable to show a warning
		 * @param fieldName
		 */
		const checkIfFieldIsNotTrainable = (fieldName: string) => {
			const field = props.documentClassFields.find((field: DocumentField) => getTranslatedDescription(field) === fieldName);
			return (field && !field.trainable);
		}

		return {
			t,
			selection,
			deleteTrainingRow,
			changeTrainingRow,
			onRowSelect,
			checkIfFieldIsNotTrainable,
		}
	},
});
</script>

<style scoped>

::v-deep(.header-center div.p-column-header-content) {
	justify-content: center;
}

.icon-error {
	color: var(--error-color);
}

::v-deep(td div.column-cell) {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

</style>
